import type { Pixel } from '@/data';
import type { ImageDimensions } from '@/formats/inputSourcesParsers/SourceParser';
import type { Image3DPageFormat, Image3DRowFormat, Image3DSyncFormat } from '@/formats/UnitParser';
import {
  rendering,
  type model, Settings, importer,
  LayoutMode,
} from '@coderline/alphatab';
import type { IClient } from './IClient';

type SvgChunk = {
  svg: SVGElement,
  width: number,
  height:number,
};

export class AlphaTabService {
  public static readonly QuarterTime: number = 960;

  private rawScore : Uint8Array;

  private client : IClient;

  alphatabSettings : Settings;

  model? : model.Score;

  private renderer? : rendering.ScoreRenderer;

  private svgChunks : SVGElement[] = [];

  private renderResult? : rendering.RenderFinishedEventArgs;

  private renderLinear : boolean;

  constructor(
    rawScore : Uint8Array,
    client : IClient,
    linear = true,
    alphatabSettings = new Settings(),
  ) {
    this.rawScore = rawScore;
    this.client = client;
    this.renderLinear = linear;
    this.alphatabSettings = alphatabSettings;
  }

  async loadAndRender() : Promise<void> {
    await this.loadModel();
    await this.render();
  }

  private async loadModel() : Promise<model.Score> {
    if (this.renderLinear) {
      this.alphatabSettings.display.layoutMode = LayoutMode.Horizontal;
    }
    this.alphatabSettings.display.systemLabelPaddingLeft = 30;

    const score: model.Score = importer.ScoreLoader
      .loadScoreFromBytes(this.rawScore, this.alphatabSettings);
      // eslint-disable-next-line no-param-reassign
    score.tracks.forEach((t) => t.staves.forEach((s) => { s.showStandardNotation = false; }));
    this.alphatabSettings.core.engine = 'svg';
    this.alphatabSettings.display.padding = [0, 0];
    this.model = score;
    return score;
  }

  /*
  Each is a part of the final image. The chunks are only assembled in the get svgImage() method.
  Each chunk is rendered separately and thus need to be translated so they don't everlap each others
  */
  private renderSvgChunks() :void {
    const { renderer } = this;
    if (renderer) {
      renderer.preRender.on(() => {
        this.svgChunks = []; // clear on new rendering
      });
      renderer.partialLayoutFinished.on((r) => {
        renderer.renderResult(r.id);
      });
      const parser = new DOMParser();
      renderer.partialRenderFinished.on((r) => {
        const svg = parser.parseFromString(r.renderResult as string, 'text/html').body.firstChild as SVGElement;
        AlphaTabService.translateSvgChunk(
          svg,
          r.x,
          r.y,
        );
        this.svgChunks.push(svg);
      });
    }
  }

  async render() : Promise<string> {
    const renderer = new rendering.ScoreRenderer(this.alphatabSettings);
    this.renderer = renderer;
    renderer.width = 1500;
    this.renderSvgChunks();
    const value = new Promise<rendering.RenderFinishedEventArgs>((resolve) => {
      renderer.renderFinished.on((e) => { resolve(e); });
    });
    renderer.renderScore(this.model!, [0]);
    this.renderResult = await value;
    return this.svgImage;
  }

  /*
  Staves positions use the boundsLookup provided by alphatab renderer
  The X coordinates work as expected but the Y coordinates seem to
  have an offset of 40ish pixels for some reasons */
  get positions() : Image3DSyncFormat {
    const page : Image3DPageFormat = {
      image: 'Genrated by alphaTab',
      rows: [],
    };
    if (this.renderer) {
      if (this.renderer.boundsLookup !== null) {
        const lookup : rendering.BoundsLookup|null = this.renderer.boundsLookup;

        lookup.staffSystems.forEach(
          (staffSystem) => {
            const row : Image3DRowFormat = {
              staves: [],
              y: staffSystem.realBounds.y,
              height: staffSystem.realBounds.h,
              'start-x': staffSystem.realBounds.x,
              'end-x': staffSystem.realBounds.x + staffSystem.realBounds.w,
              'onset-x': [],
            };

            row.staves?.push(
              {
                top: staffSystem.realBounds.y,
                bottom: staffSystem.realBounds.y + staffSystem.realBounds.h,
              },
            );
            // TODO multiple staves
            /* staffSystem.bars[0].bars.forEach((bar) => {
              row.staves?.push(
                {
                  top: bar.realBounds.y,
                  bottom: bar.realBounds.y + bar.realBounds.h,
                },
              );
            }); */
            staffSystem.bars.forEach((masterBar) => {
              const bar = masterBar.bars[0];
              bar.beats.forEach(
                (beat) => row['onset-x'].push(
                  {
                    onset: beat.beat.absoluteDisplayStart / AlphaTabService.QuarterTime,
                    x: beat.realBounds.x,
                  },
                ),
              );
            });
            // had to filter because somehow there are multiple staffSystems for same row ??
            if (!page.rows.find((r) => r['end-x'] === row['end-x']
              && r.height === row.height
              && r['start-x'] === row['start-x']
              && r.y === row.y)) {
              page.rows.push(row);
            }
          },
        );
      }
    }

    return { pages: [page] };
  }

  get dimensions() : ImageDimensions {
    if (this.renderResult) {
      return {
        width: this.renderResult.totalWidth as Pixel,
        height: this.renderResult.totalHeight as Pixel,
      };
    }
    return { width: 0 as Pixel, height: 0 as Pixel };
  }

  get svgImage() : string {
    return this.svgChunks.map((c) => c.outerHTML).join('\n');
  }

  private static translateSvgChunk(svgChunk:SVGElement, x:number, y:number) : void {
    svgChunk.setAttribute('transform', `translate(${x} ${y})`);
  }
}
